import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  getServiceMetadata,
  submitCatalogJob,
  CatalogJobStatus,
  ServiceMetadata,
  SubmitCatalogJobResponse,
} from '../api/services';
import { HttpError } from '../utils/http-error';

const POLL_INTERVAL_SECONDS = 15;
const POLL_INTERVAL_MS = POLL_INTERVAL_SECONDS * 1000;
const MAX_POLL_COUNT = 60;
const READ_RETRY_LIMIT = 1;

const CATALOG_STATUS_DESCRIPTIONS: Record<CatalogJobStatus, string> = {
  [CatalogJobStatus.PENDING]: 'Catalog job running',
  [CatalogJobStatus.SUCCESS]: 'Catalog job succeeded',
  [CatalogJobStatus.FAILURE]: 'Catalog job failed',
};

export function describeCatalogStatus(status: CatalogJobStatus): string {
  return CATALOG_STATUS_DESCRIPTIONS[status];
}

export function describeRequestFailure(attemptedAction: string, serviceName: string, cause: unknown): string {
  const reason = cause instanceof Error ? cause.message : String(cause);
  return `Could not ${attemptedAction} for ${serviceName}: ${reason}. Try again.`;
}

export function serviceMetadataQueryKey(serviceName: string) {
  return ['serviceMetadata', serviceName];
}

interface SubmitCatalogJobRequest {
  serviceName: string;
  forceSubmit: boolean;
}

export function useSubmitCatalogJob() {
  const queryClient = useQueryClient();

  return useMutation<SubmitCatalogJobResponse, unknown, SubmitCatalogJobRequest>(
    ({ serviceName, forceSubmit }) => submitCatalogJob(serviceName, forceSubmit),
    {
      onSuccess: async (response, { serviceName }) => {
        const queryKey = serviceMetadataQueryKey(serviceName);
        // A status read that started before this submit would otherwise resolve afterwards and
        // overwrite the submitted status with the older one.
        await queryClient.cancelQueries(queryKey);
        queryClient.setQueryData<ServiceMetadata>(queryKey, (existingMetadata) => ({
          ...(existingMetadata ?? { serviceName, apiList: [] }),
          catalogStatus: response.status,
          catalogJobErrorMessage: response.catalogJobErrorMessage,
        }));
      },
    },
  );
}

interface PollProgress {
  serviceName: string;
  consecutivePendingReads: number;
  isLimitWarningDismissed: boolean;
}

function startingPollProgress(serviceName: string): PollProgress {
  return { serviceName, consecutivePendingReads: 0, isLimitWarningDismissed: false };
}

interface UseCatalogJobResult {
  serviceMetadata: ServiceMetadata | null;
  catalogStatus: CatalogJobStatus | null;
  catalogJobErrorMessage: string | null;
  submittedJobId: string | null;
  isLoadingStatus: boolean;
  isServiceNotCataloged: boolean;
  isSubmitting: boolean;
  isPolling: boolean;
  pollCount: number;
  pollLimitReached: boolean;
  error: string | null;
  loadStatus: () => void;
  submit: () => void;
  checkJobStatus: () => void;
  startPolling: () => void;
  dismissPollLimit: () => void;
}

interface UseCatalogJobOptions {
  forceSubmit?: boolean;
}

export function useCatalogJob(
  serviceName: string,
  { forceSubmit = false }: UseCatalogJobOptions = {},
): UseCatalogJobResult {
  const [storedPollProgress, setStoredPollProgress] = useState(() => startingPollProgress(serviceName));
  const pollProgress =
    storedPollProgress.serviceName === serviceName ? storedPollProgress : startingPollProgress(serviceName);
  const hasReachedPollLimit = pollProgress.consecutivePendingReads > MAX_POLL_COUNT;

  const updatePollProgress = useCallback(
    (update: (currentProgress: PollProgress) => PollProgress) => {
      setStoredPollProgress((currentProgress) =>
        update(currentProgress.serviceName === serviceName ? currentProgress : startingPollProgress(serviceName)),
      );
    },
    [serviceName],
  );

  const restartPollProgress = useCallback(
    () => updatePollProgress(() => startingPollProgress(serviceName)),
    [updatePollProgress, serviceName],
  );

  const serviceMetadataQuery = useQuery(serviceMetadataQueryKey(serviceName), () => getServiceMetadata(serviceName), {
    enabled: Boolean(serviceName),
    staleTime: POLL_INTERVAL_MS,
    retry: (failureCount, error) => !HttpError.isNotFound(error) && failureCount < READ_RETRY_LIMIT,
    refetchInterval: (metadata, query) =>
      metadata?.catalogStatus === CatalogJobStatus.PENDING && !hasReachedPollLimit && query.state.status !== 'error'
        ? POLL_INTERVAL_MS
        : false,
    onSuccess: (metadata) =>
      updatePollProgress((currentProgress) => ({
        ...currentProgress,
        consecutivePendingReads:
          metadata.catalogStatus === CatalogJobStatus.PENDING ? currentProgress.consecutivePendingReads + 1 : 0,
      })),
  });

  const submitCatalogJobMutation = useSubmitCatalogJob();
  const { mutate: submitCatalogJobRequest } = submitCatalogJobMutation;
  const isSubmissionForThisService = submitCatalogJobMutation.variables?.serviceName === serviceName;

  const serviceMetadata = serviceMetadataQuery.data ?? null;
  const catalogStatus = serviceMetadata?.catalogStatus ?? null;
  const isReadNotFound = serviceMetadataQuery.isError && HttpError.isNotFound(serviceMetadataQuery.error);
  const isPolling = catalogStatus === CatalogJobStatus.PENDING && !hasReachedPollLimit && !serviceMetadataQuery.isError;

  const readError =
    serviceMetadataQuery.isError && !isReadNotFound
      ? describeRequestFailure('load the catalog job status', serviceName, serviceMetadataQuery.error)
      : null;
  const submitError =
    isSubmissionForThisService && submitCatalogJobMutation.isError
      ? describeRequestFailure('submit the catalog job', serviceName, submitCatalogJobMutation.error)
      : null;

  const submitForService = useCallback(
    (shouldForceSubmit: boolean) => {
      if (!serviceName) return;
      submitCatalogJobRequest({ serviceName, forceSubmit: shouldForceSubmit }, { onSuccess: restartPollProgress });
    },
    [serviceName, submitCatalogJobRequest, restartPollProgress],
  );

  const submit = useCallback(() => submitForService(forceSubmit), [submitForService, forceSubmit]);

  const checkJobStatus = useCallback(() => submitForService(false), [submitForService]);

  const { refetch: refetchServiceMetadata } = serviceMetadataQuery;

  const loadStatus = useCallback(() => {
    refetchServiceMetadata();
  }, [refetchServiceMetadata]);

  const startPolling = useCallback(() => {
    restartPollProgress();
    refetchServiceMetadata();
  }, [restartPollProgress, refetchServiceMetadata]);

  const dismissPollLimit = useCallback(
    () => updatePollProgress((currentProgress) => ({ ...currentProgress, isLimitWarningDismissed: true })),
    [updatePollProgress],
  );

  return {
    serviceMetadata,
    catalogStatus,
    catalogJobErrorMessage: serviceMetadata?.catalogJobErrorMessage ?? null,
    submittedJobId: isSubmissionForThisService ? (submitCatalogJobMutation.data?.jobId ?? null) : null,
    isLoadingStatus: serviceMetadataQuery.isFetching,
    isServiceNotCataloged: isReadNotFound || (serviceMetadataQuery.isSuccess && !catalogStatus),
    isSubmitting: isSubmissionForThisService && submitCatalogJobMutation.isLoading,
    isPolling,
    pollCount: pollProgress.consecutivePendingReads,
    pollLimitReached:
      catalogStatus === CatalogJobStatus.PENDING && hasReachedPollLimit && !pollProgress.isLimitWarningDismissed,
    error: submitError ?? readError,
    loadStatus,
    submit,
    checkJobStatus,
    startPolling,
    dismissPollLimit,
  };
}
